// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🗓️ VIRTUAL TRAININGS SERVICE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import { supabase } from '../lib/supabase';
import type {
  VirtualTraining,
  VirtualTrainingWithExercises,
  VirtualTrainingExercise,
  VirtualTrainingSession,
  VirtualTrainingSessionWithDetails,
  CreateVirtualTrainingParams,
} from '../types';

// ============================================================================
// CREATE
// ============================================================================

export async function createVirtualTraining(
  params: CreateVirtualTrainingParams
): Promise<VirtualTrainingWithExercises> {
  // Create virtual training
  const { data: training, error: trainingError } = await supabase
    .from('virtual_trainings')
    .insert({
      created_by: params.created_by,
      club_id: params.club_id || null,
      title: params.title,
      description: params.description || null,
      target_skill_level: params.target_skill_level || null,
      estimated_duration_minutes: params.estimated_duration_minutes || null,
      is_template: params.is_template || false,
    })
    .select()
    .single();

  if (trainingError) throw trainingError;

  // Create exercise associations
  if (params.exercises && params.exercises.length > 0) {
    const exerciseInserts = params.exercises.map(ex => ({
      virtual_training_id: training.id,
      exercise_id: ex.exercise_id,
      order_index: ex.order_index,
      target_score: ex.target_score || null,
      repetitions: ex.repetitions || 1,
    }));

    const { error: exercisesError } = await supabase
      .from('virtual_training_exercises')
      .insert(exerciseInserts);

    if (exercisesError) throw exercisesError;
  }

  // Fetch complete training with exercises
  return getVirtualTraining(training.id) as Promise<VirtualTrainingWithExercises>;
}

// ============================================================================
// READ
// ============================================================================

// Get single virtual training
export async function getVirtualTraining(
  id: number
): Promise<VirtualTrainingWithExercises | null> {
  const { data, error } = await supabase
    .from('virtual_trainings')
    .select(`
      *,
      creator:players!created_by(id, prename, name, profile_picture_url),
      club:clubs(id, name),
      exercises:virtual_training_exercises(
        *,
        exercise:exercises(*)
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

// Get personal virtual trainings
export async function getPersonalVirtualTrainings(playerId: number): Promise<VirtualTraining[]> {
  const { data, error } = await supabase
    .from('virtual_trainings')
    .select('*')
    .eq('created_by', playerId)
    .is('club_id', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get club virtual trainings
export async function getClubVirtualTrainings(clubId: number): Promise<VirtualTraining[]> {
  const { data, error } = await supabase
    .from('virtual_trainings')
    .select('*')
    .eq('club_id', clubId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get templates
export async function getVirtualTrainingTemplates(): Promise<VirtualTraining[]> {
  const { data, error } = await supabase
    .from('virtual_trainings')
    .select('*')
    .eq('is_template', true)
    .order('usage_count', { ascending: false });

  if (error) throw error;
  return data || [];
}

// ============================================================================
// UPDATE
// ============================================================================

export async function updateVirtualTraining(
  id: number,
  updates: Partial<CreateVirtualTrainingParams>
): Promise<VirtualTraining> {
  const { data, error } = await supabase
    .from('virtual_trainings')
    .update({
      title: updates.title,
      description: updates.description,
      target_skill_level: updates.target_skill_level,
      estimated_duration_minutes: updates.estimated_duration_minutes,
      is_template: updates.is_template,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  // Update exercises if provided
  if (updates.exercises) {
    // Delete old exercises
    await supabase
      .from('virtual_training_exercises')
      .delete()
      .eq('virtual_training_id', id);

    // Insert new exercises
    const exerciseInserts = updates.exercises.map(ex => ({
      virtual_training_id: id,
      exercise_id: ex.exercise_id,
      order_index: ex.order_index,
      target_score: ex.target_score || null,
      repetitions: ex.repetitions || 1,
    }));

    await supabase
      .from('virtual_training_exercises')
      .insert(exerciseInserts);
  }

  return data;
}

// Add exercise to virtual training
export async function addExerciseToVirtualTraining(
  trainingId: number,
  exerciseId: number,
  orderIndex: number,
  targetScore?: number,
  repetitions?: number
): Promise<VirtualTrainingExercise> {
  const { data, error } = await supabase
    .from('virtual_training_exercises')
    .insert({
      virtual_training_id: trainingId,
      exercise_id: exerciseId,
      order_index: orderIndex,
      target_score: targetScore || null,
      repetitions: repetitions || 1,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Remove exercise from virtual training
export async function removeExerciseFromVirtualTraining(
  trainingId: number,
  exerciseId: number
): Promise<void> {
  const { error } = await supabase
    .from('virtual_training_exercises')
    .delete()
    .eq('virtual_training_id', trainingId)
    .eq('exercise_id', exerciseId);

  if (error) throw error;
}

// Reorder exercises
export async function reorderVirtualTrainingExercises(
  trainingId: number,
  exerciseOrder: Array<{ exercise_id: number; order_index: number }>
): Promise<void> {
  const updates = exerciseOrder.map(item =>
    supabase
      .from('virtual_training_exercises')
      .update({ order_index: item.order_index })
      .eq('virtual_training_id', trainingId)
      .eq('exercise_id', item.exercise_id)
  );

  await Promise.all(updates);
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteVirtualTraining(id: number): Promise<void> {
  const { error } = await supabase
    .from('virtual_trainings')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================================================
// SESSIONS
// ============================================================================

// Start virtual training session
export async function startVirtualTrainingSession(
  playerId: number,
  virtualTrainingId: number,
  trainingTaskId?: number
): Promise<VirtualTrainingSession> {
  const { data, error } = await supabase
    .from('virtual_training_sessions')
    .insert({
      player_id: playerId,
      virtual_training_id: virtualTrainingId,
      status: 'in_progress',
      current_exercise_index: 1,
      completed_exercises: 0,
      training_task_id: trainingTaskId || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Get virtual training session
export async function getVirtualTrainingSession(
  id: number
): Promise<VirtualTrainingSessionWithDetails | null> {
  const { data, error } = await supabase
    .from('virtual_training_sessions')
    .select(`
      *,
      player:players(id, prename, name, profile_picture_url),
      virtual_training:virtual_trainings(
        *,
        exercises:virtual_training_exercises(
          *,
          exercise:exercises(*)
        )
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

// Complete virtual training session
export async function completeVirtualTrainingSession(
  sessionId: number
): Promise<VirtualTrainingSession> {
  const { data, error } = await supabase
    .from('virtual_training_sessions')
    .update({
      status: 'completed',
      completed_at: new Date().toISOString(),
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Abandon virtual training session
export async function abandonVirtualTrainingSession(
  sessionId: number
): Promise<VirtualTrainingSession> {
  const { data, error } = await supabase
    .from('virtual_training_sessions')
    .update({
      status: 'abandoned',
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Get player's virtual training sessions
export async function getPlayerVirtualTrainingSessions(
  playerId: number,
  status?: 'in_progress' | 'completed' | 'abandoned'
): Promise<VirtualTrainingSessionWithDetails[]> {
  let query = supabase
    .from('virtual_training_sessions')
    .select(`
      *,
      virtual_training:virtual_trainings(id, title, estimated_duration_minutes)
    `)
    .eq('player_id', playerId)
    .order('started_at', { ascending: false });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

